/**
 * CockpitDataVault.js
 *
 * "Account Brain" – persistent long-term knowledge store for a SalesGod
 * account. Lives as a compact widget on top of the cockpit's right column
 * and expands into a full modal for browsing & editing.
 *
 * Storage layout (Realtime DB):
 *   World/${userId}/dataVault/
 *     entries/${entryId}/
 *       title, content, category, tags{}, pinned,
 *       files/${fileId}/{ filename, size, contentType, storagePath, downloadURL, createdAt }
 *       createdAt, updatedAt, createdBy, updatedBy
 *     customCategories/${id}/{ name }
 *
 * Storage layout (Firebase Storage):
 *   World/${userId}/dataVault/${entryId}/${timestamp}_${rand}_${safeName}
 *
 * Permissions (gated in the UI):
 *   - Owner of the active world  -> read + write
 *   - Team members               -> read only (lock indicator, all controls disabled)
 *
 * Visual language: Lucide-style stroke icons (currentColor, 1.75px),
 * unified macOS-style toolbar, borderless icon buttons, 150ms ease-out.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  ref as dbRef,
  onValue,
  push,
  set,
  update,
  remove,
} from 'firebase/database';
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { database, storage } from '../firebaseConfig';
// Global voice-capture engine. The modal + recorder + retry logic all live
// inside the engine; this file just triggers a session and persists the
// AI-structured result into the vault.
import { useSpeakEngine } from '../speakEngine';
import './CockpitDataVault.css';

// ─────────────────────────────────────────────────────────────────────────────
// Lucide-style stroke icon system. Single inline component, paths only —
// avoids a 200 kB FontAwesome dependency for what's essentially 25 glyphs.
// All icons use currentColor + stroke 1.75 so they tint to the parent's
// `color` and stay sharp at 14–22 px sizes.
// ─────────────────────────────────────────────────────────────────────────────
const ICON_PATHS = {
  brain: (
    <>
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
    </>
  ),
  pin: (
    <>
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </>
  ),
  paperclip: (
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.99 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  x: <path d="M18 6 6 18M6 6l12 12" />,
  plus: <path d="M5 12h14M12 5v14" />,
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  'arrow-left': <path d="M19 12H5M12 19l-7-7 7-7" />,
  'arrow-right': <path d="M5 12h14M12 5l7 7-7 7" />,
  'cloud-upload': (
    <>
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="M12 12v9" />
      <path d="m16 16-4-4-4 4" />
    </>
  ),
  lock: (
    <>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  'alert-circle': (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </>
  ),
  'file-text': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  'sticky-note': (
    <>
      <path d="M4 4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v10l-6 6H5a1 1 0 0 1-1-1V4Z" />
      <path d="M20 14h-5a1 1 0 0 0-1 1v5" />
      <path d="M8 8h8M8 11.5h4" />
    </>
  ),
  'file-spreadsheet': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h2M8 17h2M14 13h2M14 17h2" />
    </>
  ),
  'file-image': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <circle cx="10" cy="13" r="2" />
      <path d="m20 17-1.09-1.09a2 2 0 0 0-2.82 0L10 22" />
    </>
  ),
  'file-archive': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M10 12v.01M10 16v.01M10 20v.01" />
    </>
  ),
  'file-bar-chart': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 18v-2M12 18v-4M16 18v-6" />
    </>
  ),
  folder: (
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  ),
  'folder-open': (
    <path d="m6 14 1.45-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.55 6a2 2 0 0 1-1.94 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.93a2 2 0 0 1 1.66.9l.82 1.2a2 2 0 0 0 1.66.9H18a2 2 0 0 1 2 2v2" />
  ),
  megaphone: (
    <>
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  gem: (
    <path d="M6 3h12l4 6-10 13L2 9z M11 3 8 9l4 13 4-13-3-6 M2 9h20" />
  ),
  tag: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r=".75" fill="currentColor" />
    </>
  ),
  trophy: (
    <>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </>
  ),
  'message-square': (
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  ),
  sliders: (
    <>
      <line x1="4" x2="4" y1="21" y2="14" />
      <line x1="4" x2="4" y1="10" y2="3" />
      <line x1="12" x2="12" y1="21" y2="12" />
      <line x1="12" x2="12" y1="8" y2="3" />
      <line x1="20" x2="20" y1="21" y2="16" />
      <line x1="20" x2="20" y1="12" y2="3" />
      <line x1="2" x2="6" y1="14" y2="14" />
      <line x1="10" x2="14" y1="8" y2="8" />
      <line x1="18" x2="22" y1="16" y2="16" />
    </>
  ),
  layers: (
    <>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </>
  ),
  dot: <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />,
  check: <path d="M20 6 9 17l-5-5" />,
  'more-horizontal': (
    <>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </>
  ),
  'file-up': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6" />
      <path d="m9 15 3-3 3 3" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  pencil: (
    <>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <path d="M12 19v3" />
    </>
  ),
};

const Icon = ({ name, size = 16, strokeWidth, className, style, title }) => {
  const paths = ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth || 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title && <title>{title}</title>}
      {paths}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Default categories – starter taxonomy. Each carries a Lucide icon name.
// ─────────────────────────────────────────────────────────────────────────────
// Special category whose entries surface as a note badge in the top navigation
// (next to the account plan badge). Kept as a normal vault category so it reuses
// the full editor, autosave, files, etc. – but the header watches it live.
export const HEADER_NOTES_CATEGORY_ID = 'header-notes';

const DEFAULT_CATEGORIES = [
  { id: HEADER_NOTES_CATEGORY_ID, name: 'Header notes', icon: 'sticky-note', hint: 'Shows as a note icon in the top navigation. Short intro text + bullet points.' },
  { id: 'business', name: 'My Business', icon: 'layers', hint: 'What you do, your model, your story.' },
  { id: 'selling-points', name: 'Selling Points', icon: 'gem', hint: 'USPs and why you\'re different.' },
  { id: 'offer', name: 'No Brainer Offer', icon: 'tag', hint: 'The irresistible core offer.' },
  { id: 'lead-magnet', name: 'Lead Magnet', icon: 'megaphone', hint: 'Free value that attracts leads.' },
  { id: 'proof', name: 'Proof & Testimonials', icon: 'trophy', hint: 'Wins, numbers, social proof.' },
  { id: 'audience', name: 'Target Audience', icon: 'users', hint: 'Who you sell to – goals and language.' },
  { id: 'painpoints', name: 'Target Painpoints', icon: 'alert-circle', hint: 'The problems you solve for them.' },
  { id: 'cta', name: 'Call To Action', icon: 'arrow-right', hint: 'What you want them to do next.' },
  { id: 'general', name: 'General', icon: 'folder-open', hint: 'Anything else worth keeping.' },
];

/* The areas that change AI output the most – offered as a starting point
   while the vault is still empty. */
const STARTER_CATEGORY_IDS = ['business', 'audience', 'offer'];

const ALLOWED_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'md',
  'zip', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic',
]);
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const ALLOWED_ACCEPT = [
  'application/pdf',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.rtf', '.md', '.zip',
  'image/*',
].join(',');

const sanitizeFileName = (name) => {
  if (!name || typeof name !== 'string') return 'file';
  return name.trim().slice(0, 180).replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_+/g, '_');
};

const getExtension = (name) => {
  if (!name || typeof name !== 'string') return '';
  const idx = name.lastIndexOf('.');
  return idx === -1 ? '' : name.slice(idx + 1).toLowerCase();
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const fileIconFor = (ext) => {
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'file-spreadsheet';
  if (['ppt', 'pptx'].includes(ext)) return 'file-bar-chart';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'].includes(ext)) return 'file-image';
  if (ext === 'zip') return 'file-archive';
  return 'file-text';
};

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight note markup. A tiny, AI-friendly subset of Markdown so entries
// can carry structure (headings, bold, bullets, callouts, spacing) without a
// heavy editor dependency. Documented in docs/notes-and-files-import-schema.md.
//
//   # Heading          → section title
//   ## Subheading      → smaller title
//   - item             → bullet list (consecutive lines group together)
//   > text             → highlighted callout
//   **bold**           → bold
//   _italic_           → italic
//   (blank line)       → spacing between blocks
// ─────────────────────────────────────────────────────────────────────────────

// Inline pass: bold (**…**) and italic (_…_). Returns an array of React nodes.
function renderInline(text, keyPrefix) {
  if (!text) return [text];
  const tokenRe = /(\*\*[^*]+\*\*|_[^_]+_)/g;
  const parts = text.split(tokenRe);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (/^_[^_]+_$/.test(part)) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

// Block pass: groups lines into headings / lists / callouts / paragraphs.
function renderRichText(content) {
  if (!content || !content.trim()) return null;
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let list = null;     // accumulating <ul> items
  let quote = null;    // accumulating callout lines
  let para = null;     // accumulating paragraph lines

  const flushList = () => { if (list) { blocks.push({ type: 'ul', items: list }); list = null; } };
  const flushQuote = () => { if (quote) { blocks.push({ type: 'quote', lines: quote }); quote = null; } };
  const flushPara = () => { if (para) { blocks.push({ type: 'p', lines: para }); para = null; } };
  const flushAll = () => { flushList(); flushQuote(); flushPara(); };

  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, '');
    if (!line.trim()) { flushAll(); return; }
    if (/^#\s+/.test(line)) { flushAll(); blocks.push({ type: 'h1', text: line.replace(/^#\s+/, '') }); return; }
    if (/^##\s+/.test(line)) { flushAll(); blocks.push({ type: 'h2', text: line.replace(/^##\s+/, '') }); return; }
    if (/^[-*]\s+/.test(line)) { flushQuote(); flushPara(); (list = list || []).push(line.replace(/^[-*]\s+/, '')); return; }
    if (/^>\s?/.test(line)) { flushList(); flushPara(); (quote = quote || []).push(line.replace(/^>\s?/, '')); return; }
    flushList(); flushQuote(); (para = para || []).push(line);
  });
  flushAll();

  return blocks.map((b, i) => {
    const k = `b-${i}`;
    if (b.type === 'h1') return <h3 key={k} className="vault__richH1">{renderInline(b.text, k)}</h3>;
    if (b.type === 'h2') return <h4 key={k} className="vault__richH2">{renderInline(b.text, k)}</h4>;
    if (b.type === 'ul') {
      return (
        <ul key={k} className="vault__richUl">
          {b.items.map((it, j) => <li key={`${k}-${j}`}>{renderInline(it, `${k}-${j}`)}</li>)}
        </ul>
      );
    }
    if (b.type === 'quote') {
      return (
        <blockquote key={k} className="vault__richQuote">
          {b.lines.map((ln, j) => (
            <React.Fragment key={`${k}-${j}`}>
              {renderInline(ln, `${k}-${j}`)}
              {j < b.lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </blockquote>
      );
    }
    return (
      <p key={k} className="vault__richP">
        {b.lines.map((ln, j) => (
          <React.Fragment key={`${k}-${j}`}>
            {renderInline(ln, `${k}-${j}`)}
            {j < b.lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

// Strip markup down to clean prose for list snippets / search.
function stripMarkup(content) {
  if (!content) return '';
  return content
    .replace(/\r\n/g, '\n')
    .replace(/^#{1,2}\s+/gm, '')
    .replace(/^[-*]\s+/gm, '• ')
    .replace(/^>\s?/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\n{2,}/g, '  ')
    .replace(/\n/g, ' ')
    .trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Import / export – a single JSON envelope carries the category taxonomy plus a
// list of entries. The template is meant to be handed to an AI: it fills the
// `entries` array (using the category ids + markup syntax) and the user re-imports
// it to bulk-create notes. See docs/notes-and-files-import-schema.md.
// ─────────────────────────────────────────────────────────────────────────────
const VAULT_SCHEMA_VERSION = 1;

function buildVaultTemplate(allCategories) {
  return {
    schemaVersion: VAULT_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    docs: 'docs/notes-and-files-import-schema.md',
    markup: {
      heading: '# Heading',
      subheading: '## Subheading',
      bold: '**bold**',
      italic: '_italic_',
      bullet: '- list item',
      callout: '> highlighted callout',
      spacing: 'Separate blocks with a blank line.',
    },
    categories: allCategories
      .filter((c) => !c.custom)
      .map((c) => ({ id: c.id, name: c.name, hint: c.hint || '' })),
    entries: [
      {
        title: 'Company overview',
        category: 'business',
        pinned: true,
        tags: ['positioning'],
        content:
          '# What we do\n\nWe help **B2B SaaS teams** turn cold traffic into booked demos.\n\n## Why it matters\n\n- Done-for-you outreach\n- _Predictable_ pipeline\n\n> Replace this example with real, specific information.',
      },
    ],
  };
}

function triggerJsonDownload(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Normalise + validate a parsed JSON payload into importable entries.
function parseVaultImport(json, validCatIds) {
  if (!json || typeof json !== 'object') throw new Error('File is not valid JSON.');
  const rawEntries = Array.isArray(json) ? json : json.entries;
  if (!Array.isArray(rawEntries)) {
    throw new Error('Missing an "entries" array. Use the downloaded template as a starting point.');
  }
  const entries = rawEntries
    .filter((e) => e && typeof e === 'object')
    .map((e) => {
      const tagsArr = Array.isArray(e.tags)
        ? e.tags
        : (e.tags && typeof e.tags === 'object') ? Object.keys(e.tags) : [];
      const tags = tagsArr
        .map((t) => String(t).trim().toLowerCase().replace(/^#/, '').replace(/\s+/g, '-'))
        .filter(Boolean)
        .reduce((acc, t) => { acc[t] = true; return acc; }, {});
      const category = validCatIds.includes(e.category) ? e.category : 'general';
      return {
        title: (e.title && String(e.title).trim()) || 'Untitled',
        content: typeof e.content === 'string' ? e.content : '',
        category,
        pinned: !!e.pinned,
        tags,
      };
    })
    .filter((e) => e.title !== 'Untitled' || e.content.trim());
  if (!entries.length) throw new Error('No usable entries found in the file.');
  return entries;
}

async function importVaultEntries(userId, currentUserId, entries) {
  let created = 0;
  for (const e of entries) {
    // eslint-disable-next-line no-await-in-loop
    await createEntry(userId, currentUserId, e);
    created += 1;
  }
  return created;
}

// ─────────────────────────────────────────────────────────────────────────────
// Live data hook
// ─────────────────────────────────────────────────────────────────────────────
function useDataVault(userId) {
  const [entries, setEntries] = useState({});
  const [customCategories, setCustomCategories] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(true); return; }
    const entriesRef = dbRef(database, `World/${userId}/dataVault/entries`);
    const catsRef = dbRef(database, `World/${userId}/dataVault/customCategories`);

    const unsubEntries = onValue(entriesRef, (snap) => {
      setEntries(snap.exists() ? snap.val() : {});
      setLoading(false);
    }, (err) => {
      console.warn('[DataVault] entries subscribe error', err);
      setLoading(false);
    });
    const unsubCats = onValue(catsRef, (snap) => {
      setCustomCategories(snap.exists() ? snap.val() : {});
    });

    return () => {
      unsubEntries();
      unsubCats();
    };
  }, [userId]);

  return { entries, customCategories, loading };
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────────────────
async function createEntry(userId, currentUserId, data) {
  const now = new Date().toISOString();
  const newRef = push(dbRef(database, `World/${userId}/dataVault/entries`));
  await set(newRef, {
    title: data.title || 'Untitled',
    content: data.content || '',
    category: data.category || 'general',
    tags: data.tags || {},
    pinned: !!data.pinned,
    files: {},
    createdAt: now,
    updatedAt: now,
    createdBy: currentUserId || null,
    updatedBy: currentUserId || null,
  });
  return newRef.key;
}

async function updateEntry(userId, currentUserId, entryId, patch) {
  await update(dbRef(database, `World/${userId}/dataVault/entries/${entryId}`), {
    ...patch,
    updatedAt: new Date().toISOString(),
    updatedBy: currentUserId || null,
  });
}

async function deleteEntry(userId, entryId, files) {
  if (files && typeof files === 'object') {
    await Promise.all(
      Object.values(files).map(async (f) => {
        if (!f || !f.storagePath) return;
        try { await deleteObject(storageRef(storage, f.storagePath)); }
        catch (e) { /* ignore – fall through and remove metadata anyway */ }
      })
    );
  }
  await remove(dbRef(database, `World/${userId}/dataVault/entries/${entryId}`));
}

async function addCustomCategory(userId, name) {
  const newRef = push(dbRef(database, `World/${userId}/dataVault/customCategories`));
  await set(newRef, { name });
  return newRef.key;
}

async function removeCustomCategory(userId, catId) {
  await remove(dbRef(database, `World/${userId}/dataVault/customCategories/${catId}`));
}

function uploadFileToEntry(userId, entryId, file, onProgress) {
  return new Promise((resolve, reject) => {
    const safeName = sanitizeFileName(file.name);
    const path = `World/${userId}/dataVault/${entryId}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
    const fileRef = storageRef(storage, path);
    const task = uploadBytesResumable(fileRef, file, {
      contentType: file.type || 'application/octet-stream',
      customMetadata: { originalFilename: file.name },
    });
    task.on(
      'state_changed',
      (snap) => {
        if (snap.totalBytes > 0 && onProgress) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({
            filename: file.name,
            size: file.size,
            contentType: file.type || 'application/octet-stream',
            storagePath: path,
            downloadURL: url,
            createdAt: new Date().toISOString(),
          });
        } catch (e) { reject(e); }
      }
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main entry point – renders either the compact widget (in cockpit's right
// column) or the full-screen page (when expanded). Mode is controlled by the
// parent so the page can replace the entire cockpit surface, not float on top.
// ─────────────────────────────────────────────────────────────────────────────
export default function CockpitDataVault({
  userId,
  currentUserId,
  canEdit,
  mode = 'compact',     // 'compact' | 'fullscreen'
  onExpand,             // called from compact mode when user clicks the widget
  onClose,              // called from fullscreen mode to return to cockpit
  closing = false,      // fullscreen-mode flag → drives the exit animation
}) {
  const { entries, customCategories, loading } = useDataVault(userId);
  // Voice-memo capture is handled by the global SpeakEngine, mounted once in
  // WorldLayout. This component only fires openCapture() and receives the
  // structured result via a callback — no modal lives here anymore.
  const { openCapture } = useSpeakEngine();
  const [voiceMemoToast, setVoiceMemoToast] = useState('');
  useEffect(() => {
    if (!voiceMemoToast) return undefined;
    const t = setTimeout(() => setVoiceMemoToast(''), 4200);
    return () => clearTimeout(t);
  }, [voiceMemoToast]);

  const entriesArr = useMemo(() => {
    return Object.entries(entries || {})
      .map(([id, e]) => ({ id, ...e }))
      .sort((a, b) => {
        if (!!b.pinned - !!a.pinned !== 0) return !!b.pinned - !!a.pinned;
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      });
  }, [entries]);

  const counts = useMemo(() => {
    let files = 0;
    let pinned = 0;
    // Entries that actually carry knowledge. A bare "Untitled" row without text
    // or files still counts as an empty vault for the compact widget.
    let filled = 0;
    entriesArr.forEach((e) => {
      if (e.pinned) pinned += 1;
      const entryFiles = e.files ? Object.keys(e.files).length : 0;
      files += entryFiles;
      if (entryFiles > 0 || (e.content && e.content.trim())) filled += 1;
    });
    return { entries: entriesArr.length, files, pinned, filled };
  }, [entriesArr]);

  const preview = entriesArr.slice(0, 3);

  // How much of the starter taxonomy actually carries content. This is the one
  // number that tells a user whether the brain is usable or still a stub.
  const coverage = useMemo(() => {
    const filled = new Set();
    entriesArr.forEach((e) => {
      const hasSubstance = (e.content && e.content.trim().length > 0) || (e.files && Object.keys(e.files).length > 0);
      if (hasSubstance && e.category) filled.add(e.category);
    });
    const missing = DEFAULT_CATEGORIES.filter((c) => !filled.has(c.id));
    return {
      filledCount: DEFAULT_CATEGORIES.filter((c) => filled.has(c.id)).length,
      total: DEFAULT_CATEGORIES.length,
      next: missing.find((c) => c.id !== HEADER_NOTES_CATEGORY_ID) || missing[0] || null,
    };
  }, [entriesArr]);

  const lastUpdated = useMemo(
    () => entriesArr.reduce((latest, e) => (
      e.updatedAt && (!latest || e.updatedAt > latest) ? e.updatedAt : latest
    ), null),
    [entriesArr]
  );

  const allCategories = useMemo(() => [
    ...DEFAULT_CATEGORIES,
    ...Object.entries(customCategories || {}).map(([id, c]) => ({
      id, name: c.name || 'Untitled', icon: 'folder', hint: '', custom: true,
    })),
  ], [customCategories]);

  // Opens a SpeakEngine session with the notesAndFiles preset. The engine
  // records, transcribes, structures via Claude, and hands us the (user-
  // confirmed) entries + new categories in `onResult`. All persistence to
  // RTDB happens here — the engine is intentionally storage-agnostic.
  const openVoiceMemo = useCallback(() => {
    if (!canEdit) return;
    openCapture({
      preset: 'notesAndFiles',
      // Pass the account's live category list (defaults + custom) so the
      // AI picks from what the user actually sees, not a stale server-side
      // fallback.
      context: { categories: allCategories },
      onResult: async ({ entries: proposed, newCategories }) => {
        if (!proposed?.length) return '';

        // 1. Create any brand-new custom categories that at least one selected
        //    entry references. Build tempId → real DB id map.
        const tempIdToRealId = new Map();
        for (const cat of newCategories || []) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const catRef = push(dbRef(database, `World/${userId}/dataVault/customCategories`));
            // eslint-disable-next-line no-await-in-loop
            await set(catRef, { name: cat.name });
            tempIdToRealId.set(cat.tempId, catRef.key);
          } catch (err) {
            console.warn('[vault.voiceMemo] category creation failed', cat.name, err.message);
          }
        }

        // 2. Persist the confirmed entries with the standard vault schema.
        const now = new Date().toISOString();
        let created = 0;
        for (const e of proposed) {
          const catId = tempIdToRealId.get(e.categoryId) || e.categoryId || 'general';
          const tagMap = Array.isArray(e.tags)
            ? e.tags.reduce((acc, t) => { acc[t] = true; return acc; }, {})
            : {};
          try {
            // eslint-disable-next-line no-await-in-loop
            const entryRef = push(dbRef(database, `World/${userId}/dataVault/entries`));
            // eslint-disable-next-line no-await-in-loop
            await set(entryRef, {
              title: e.title || 'Untitled',
              content: e.content || '',
              category: catId,
              tags: tagMap,
              pinned: false,
              files: {},
              createdAt: now,
              updatedAt: now,
              createdBy: currentUserId || null,
              updatedBy: currentUserId || null,
              source: { kind: 'voice-memo' },
            });
            created += 1;
          } catch (err) {
            console.warn('[vault.voiceMemo] entry creation failed', err.message);
          }
        }

        if (created > 0) {
          setVoiceMemoToast(`${created} ${created === 1 ? 'entry' : 'entries'} added from voice memo.`);
        }
        return created > 0
          ? `${created} ${created === 1 ? 'entry' : 'entries'} added to your brain`
          : '';
      },
    });
  }, [canEdit, openCapture, allCategories, userId, currentUserId]);

  // Fullscreen page – replaces the cockpit surface entirely.
  if (mode === 'fullscreen') {
    return (
      <VaultPage
        onClose={onClose}
        closing={closing}
        userId={userId}
        currentUserId={currentUserId}
        canEdit={canEdit}
        entriesArr={entriesArr}
        allCategories={allCategories}
        customCategories={customCategories}
        onOpenVoiceMemo={openVoiceMemo}
        voiceMemoToast={voiceMemoToast}
      />
    );
  }

  // Compact widget – sits in cockpit's right column, click expands.
  const isEmpty = !loading && counts.filled === 0;
  const coveragePct = Math.round((coverage.filledCount / coverage.total) * 100);

  // The compact widget's voice button should feel "instant": we open the
  // engine directly instead of first expanding to fullscreen. Once the
  // memo is saved, the user's newly-created entries will already be
  // visible in the compact preview via the live RTDB subscription.
  const openVoiceFromCompact = (e) => {
    e.stopPropagation();
    openVoiceMemo();
  };

  return (
    <>
    <section
      className={`vault${isEmpty ? ' vault--empty' : ''}`}
      onClick={() => onExpand?.()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onExpand?.(); }}
    >
      <span className="vault__glow" aria-hidden />

      <header className="vault__head">
        <div className="vault__headBlock">
          <h2 className="vault__title">
            Notes &amp; Files
            <span className="vault__titleBadge">Account brain</span>
            {!canEdit && (
              <span className="vault__lockMini" title="Read-only">
                <Icon name="lock" size={10} strokeWidth={2} />
              </span>
            )}
          </h2>
          <p className="vault__subtitle">
            Reused by emails, research and offers.
          </p>
        </div>
        <Icon
          name="chevron-right"
          size={16}
          strokeWidth={1.75}
          className="vault__headChevron"
        />
      </header>

      {loading ? (
        <div className="vault__previewEmpty">Loading…</div>
      ) : isEmpty ? (
        <div className="vault__starter">
          <span className="vault__starterBadge">
            <Icon name="alert-circle" size={11} strokeWidth={2.1} />
            {entriesArr.length > 0 ? 'Nothing captured yet' : 'Still empty'}
          </span>
          <h3 className="vault__starterTitle">
            {canEdit ? 'Give SalesGod Memory' : 'Nothing saved here yet'}
          </h3>
          {canEdit ? (
            <>
              <p className="vault__starterText">
                Write your business, audience and offer once. SalesGod uses it everywhere.
              </p>
              <ul className="vault__starterPerks">
                <li><Icon name="message-square" size={12} /> Emails sound like you</li>
                <li><Icon name="gem" size={12} /> Offers stay on-message</li>
              </ul>
              <span className="vault__starterLabel">Start with</span>
              <div className="vault__starterChips">
                {STARTER_CATEGORY_IDS.map((id) => {
                  const cat = DEFAULT_CATEGORIES.find((c) => c.id === id);
                  if (!cat) return null;
                  return (
                    <span key={cat.id} className="vault__starterChip" title={cat.hint}>
                      <Icon name={cat.icon} size={11} /> {cat.name}
                    </span>
                  );
                })}
              </div>
              <div className="vault__starterCtaRow">
                <button
                  type="button"
                  className="vault__starterCta"
                  onClick={(e) => { e.stopPropagation(); onExpand?.(); }}
                >
                  <Icon name="plus" size={13} strokeWidth={2.3} /> Write your first note
                </button>
                <button
                  type="button"
                  className="vault__starterCta vault__starterCta--voice"
                  onClick={openVoiceFromCompact}
                  title="Record a voice memo — AI organises it for you"
                >
                  <Icon name="mic" size={13} strokeWidth={2.2} /> Speak it instead
                </button>
              </div>
              <span className="vault__starterFoot">Takes about 5 minutes — or just talk for 2</span>
            </>
          ) : (
            <p className="vault__starterText">
              The account owner hasn't captured any business knowledge yet.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="vault__coverage">
            <div className="vault__coverageHead">
              <span className="vault__coverageLabel">Brain coverage</span>
              <span className="vault__coverageValue">{coveragePct}%</span>
            </div>
            <div className="vault__coverageTrack">
              <div
                className="vault__coverageBar"
                style={{ width: `${coveragePct}%` }}
                role="progressbar"
                aria-valuenow={coveragePct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            {coverage.next && canEdit && (
              <span className="vault__coverageNext">
                <Icon name="arrow-right" size={11} strokeWidth={2} />
                Next up: {coverage.next.name}
              </span>
            )}
          </div>

          <ul className="vault__preview">
            {preview.map((e) => {
              const cat = allCategories.find((c) => c.id === e.category);
              const fileCount = e.files ? Object.keys(e.files).length : 0;
              const snippet = stripMarkup(e.content);
              return (
                <li key={e.id} className="vault__previewItem">
                  <span className="vault__previewTop">
                    {e.pinned && <span className="vault__previewPin"><Icon name="pin" size={10} /></span>}
                    <span className="vault__previewTitle">{e.title || 'Untitled'}</span>
                    <span className="vault__previewMeta">
                      {fileCount > 0 && (
                        <span className="vault__previewFiles">
                          <Icon name="paperclip" size={10} /> {fileCount}
                        </span>
                      )}
                      <span className="vault__previewTime">{formatRelative(e.updatedAt)}</span>
                    </span>
                  </span>
                  {snippet && (
                    <span className="vault__previewSnippet">
                      {snippet.length > 92 ? `${snippet.slice(0, 92)}…` : snippet}
                    </span>
                  )}
                  {cat && <span className="vault__previewCat">{cat.name}</span>}
                </li>
              );
            })}
          </ul>

          <footer className="vault__foot" aria-label="Vault summary">
            {counts.pinned > 0 && (
              <>
                <span className="vault__footPin">
                  <Icon name="pin" size={10} /> {counts.pinned} pinned
                </span>
                <span className="vault__dot" aria-hidden />
              </>
            )}
            {lastUpdated && <span>Updated {formatRelative(lastUpdated)}</span>}
            <span className="vault__footOpen">
              Open brain <Icon name="arrow-right" size={11} strokeWidth={2} />
            </span>
          </footer>
        </>
      )}
    </section>
    {voiceMemoToast && (
      <div className="vault__voiceToast" role="status">
        <Icon name="check" size={13} strokeWidth={2.4} />
        {voiceMemoToast}
      </div>
    )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fullscreen page – replaces the cockpit surface. Single unified toolbar
// (back + title + search + actions in one row), Apple Notes-style 2-pane
// body underneath.
// ─────────────────────────────────────────────────────────────────────────────
function VaultPage({
  onClose,
  closing,
  userId,
  currentUserId,
  canEdit,
  entriesArr,
  allCategories,
  customCategories,
  onOpenVoiceMemo,
  voiceMemoToast,
}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [showCatManager, setShowCatManager] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { entries, fileName }
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importToast, setImportToast] = useState('');
  const importInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !showCatManager && !pendingImport && !importError) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showCatManager, pendingImport, importError]);

  // Close the actions menu on outside click.
  useEffect(() => {
    if (!showMenu) return undefined;
    const handler = (e) => {
      if (!e.target.closest?.('.vault__menuWrap')) setShowMenu(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [showMenu]);

  useEffect(() => {
    if (!importToast) return undefined;
    const t = setTimeout(() => setImportToast(''), 4000);
    return () => clearTimeout(t);
  }, [importToast]);

  const handleDownloadTemplate = () => {
    setShowMenu(false);
    triggerJsonDownload('notes-and-files-template.json', buildVaultTemplate(allCategories));
  };

  const handlePickImport = () => {
    setShowMenu(false);
    importInputRef.current?.click();
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const validCatIds = allCategories.map((c) => c.id);
      const entries = parseVaultImport(json, validCatIds);
      setPendingImport({ entries, fileName: file.name });
    } catch (e) {
      setImportError(e.message || 'Could not read this file.');
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    try {
      const n = await importVaultEntries(userId, currentUserId, pendingImport.entries);
      setImportToast(`${n} ${n === 1 ? 'entry' : 'entries'} imported.`);
      setPendingImport(null);
    } catch (e) {
      setImportError(e.message || 'Import failed.');
      setPendingImport(null);
    } finally {
      setImporting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entriesArr.filter((e) => {
      if (filter === 'pinned' && !e.pinned) return false;
      if (filter !== 'all' && filter !== 'pinned' && e.category !== filter) return false;
      if (!q) return true;
      const tagText = e.tags ? Object.keys(e.tags).join(' ') : '';
      return (
        (e.title || '').toLowerCase().includes(q) ||
        (e.content || '').toLowerCase().includes(q) ||
        tagText.toLowerCase().includes(q)
      );
    });
  }, [entriesArr, search, filter]);

  const grouped = useMemo(() => ({
    pinned: filtered.filter((e) => e.pinned),
    rest: filtered.filter((e) => !e.pinned),
  }), [filtered]);

  const editingEntry = editing && editing !== 'new'
    ? entriesArr.find((e) => e.id === editing)
    : null;

  return (
    <div className={`vault__page${closing ? ' vault__page--closing' : ''}`}>
      {/* Single unified toolbar – back + title left, search center, actions right */}
      <header className="vault__topbar">
        <div className="vault__topbarTitle">
          <button
            type="button"
            className="vault__pageBack"
            onClick={() => onClose?.()}
            title="Back to Cockpit (Esc)"
          >
            <Icon name="arrow-left" size={14} strokeWidth={2} />
            <span>Cockpit</span>
          </button>
          <span className="vault__topbarSep" aria-hidden />
          <span className="vault__topbarIcon"><Icon name="brain" size={14} /></span>
          <span className="vault__topbarTitleText">Notes &amp; Files</span>
          {!canEdit && (
            <span className="vault__topbarLock" title="Read-only">
              <Icon name="lock" size={10} strokeWidth={2} />
              Read-only
            </span>
          )}
        </div>

        <div className="vault__topbarSearch">
          <Icon name="search" size={13} className="vault__topbarSearchIcon" />
          <input
            type="text"
            className="vault__topbarSearchInput"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="vault__topbarSearchClear"
              onClick={() => setSearch('')}
              aria-label="Clear"
            >
              <Icon name="x" size={9} strokeWidth={2.4} />
            </button>
          )}
        </div>

        <div className="vault__topbarActions">
          {canEdit && (
            <>
              <button
                type="button"
                className="vault__voiceBtn"
                onClick={() => onOpenVoiceMemo?.()}
                title="Speak — AI organises the notes into the brain"
              >
                <Icon name="mic" size={12} strokeWidth={2.1} />
                Speak
              </button>
              <button
                type="button"
                className="vault__primaryBtn"
                onClick={() => setEditing('new')}
              >
                <Icon name="plus" size={12} strokeWidth={2.2} />
                New entry
              </button>
            </>
          )}
          <div className="vault__menuWrap">
            <button
              type="button"
              className={`vault__iconMenuBtn ${showMenu ? 'is-active' : ''}`}
              onClick={() => setShowMenu((s) => !s)}
              aria-haspopup="menu"
              aria-expanded={showMenu}
              title="More"
            >
              <Icon name="more-horizontal" size={16} />
            </button>
            {showMenu && (
              <div className="vault__menu" role="menu">
                <button type="button" role="menuitem" className="vault__menuItem" onClick={handleDownloadTemplate}>
                  <Icon name="download" size={14} />
                  <span>
                    Download JSON template
                    <small>Hand to an AI, then re-import</small>
                  </span>
                </button>
                {canEdit && (
                  <button type="button" role="menuitem" className="vault__menuItem" onClick={handlePickImport}>
                    <Icon name="file-up" size={14} />
                    <span>
                      Import from JSON
                      <small>Bulk-create entries from a file</small>
                    </span>
                  </button>
                )}
              </div>
            )}
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => {
                handleImportFile(e.target.files?.[0]);
                if (e.target) e.target.value = '';
              }}
            />
          </div>
        </div>
      </header>

      {importToast && (
        <div className="vault__toast" role="status">
          <Icon name="check" size={13} strokeWidth={2.4} />
          {importToast}
        </div>
      )}

      {voiceMemoToast && (
        <div className="vault__toast" role="status">
          <Icon name="mic" size={13} strokeWidth={2.4} />
          {voiceMemoToast}
        </div>
      )}

      <div className="vault__pageBody">
          <aside className="vault__sidebar">
            <button
              type="button"
              className={`vault__filterChip ${filter === 'all' ? 'is-active' : ''}`}
              onClick={() => setFilter('all')}
            >
              <Icon name="layers" size={13} />
              <span>All</span>
              <span className="vault__filterCount">{entriesArr.length}</span>
            </button>
            <button
              type="button"
              className={`vault__filterChip ${filter === 'pinned' ? 'is-active' : ''}`}
              onClick={() => setFilter('pinned')}
            >
              <Icon name="pin" size={13} />
              <span>Pinned</span>
              <span className="vault__filterCount">
                {entriesArr.filter((e) => e.pinned).length}
              </span>
            </button>
            <div className="vault__sidebarLabel">Categories</div>
            {allCategories.map((c) => {
              const count = entriesArr.filter((e) => e.category === c.id).length;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`vault__filterChip ${filter === c.id ? 'is-active' : ''}`}
                  onClick={() => setFilter(c.id)}
                  title={c.hint || c.name}
                >
                  <Icon name={c.icon} size={13} />
                  <span>{c.name}</span>
                  {c.custom && <span className="vault__customMark" title="Custom" />}
                  <span className="vault__filterCount">{count}</span>
                </button>
              );
            })}
            {canEdit && (
              <button
                type="button"
                className="vault__sidebarManage"
                onClick={() => setShowCatManager(true)}
              >
                <Icon name="sliders" size={12} />
                <span>Manage categories</span>
              </button>
            )}
          </aside>

          <div className="vault__main">
            {editing === 'new' || editingEntry ? (
              <EntryEditor
                key={editingEntry?.id || 'new'}
                userId={userId}
                currentUserId={currentUserId}
                allCategories={allCategories}
                entry={editingEntry}
                isNew={editing === 'new'}
                canEdit={canEdit}
                onClose={() => setEditing(null)}
                onDeleted={() => setEditing(null)}
                onCreated={(newId) => setEditing(newId)}
              />
            ) : filtered.length === 0 ? (
              <div className="vault__empty">
                <h3>{search ? 'Nothing matches' : 'No entries yet'}</h3>
                <p>
                  {search
                    ? 'Try a different search term or clear the filter.'
                    : canEdit
                      ? 'Capture brand voice, personas, pricing, case studies – then reference them when building campaigns.'
                      : 'The account owner can add knowledge here.'}
                </p>
                {canEdit && !search && (
                  <div className="vault__emptyCtaRow">
                    <button type="button" className="vault__primaryBtn" onClick={() => setEditing('new')}>
                      <Icon name="plus" size={12} strokeWidth={2.2} /> Create first entry
                    </button>
                    <button
                      type="button"
                      className="vault__voiceBtn vault__voiceBtn--large"
                      onClick={() => onOpenVoiceMemo?.()}
                      title="Just talk — AI organises everything"
                    >
                      <Icon name="mic" size={13} strokeWidth={2.1} /> Speak your notes
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="vault__entryGroups">
                {grouped.pinned.length > 0 && (
                  <>
                    <div className="vault__groupHeader">Pinned</div>
                    <ul className="vault__entryList">
                      {grouped.pinned.map((e) => (
                        <EntryRow
                          key={e.id}
                          entry={e}
                          allCategories={allCategories}
                          onOpen={() => setEditing(e.id)}
                        />
                      ))}
                    </ul>
                  </>
                )}
                {grouped.rest.length > 0 && (
                  <>
                    {grouped.pinned.length > 0 && <div className="vault__groupHeader">Recent</div>}
                    <ul className="vault__entryList">
                      {grouped.rest.map((e) => (
                        <EntryRow
                          key={e.id}
                          entry={e}
                          allCategories={allCategories}
                          onOpen={() => setEditing(e.id)}
                        />
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

      {showCatManager && (
        <CategoryManager
          userId={userId}
          customCategories={customCategories}
          entriesArr={entriesArr}
          onClose={() => setShowCatManager(false)}
        />
      )}

      {pendingImport && (
        <ConfirmDialog
          title={`Import ${pendingImport.entries.length} ${pendingImport.entries.length === 1 ? 'entry' : 'entries'}?`}
          body={importing
            ? 'Creating entries…'
            : `From "${pendingImport.fileName}". New notes will be added – existing ones are untouched.`}
          confirmLabel={importing ? 'Importing…' : 'Import'}
          onCancel={() => { if (!importing) setPendingImport(null); }}
          onConfirm={handleConfirmImport}
        />
      )}

      {importError && (
        <ConfirmDialog
          title="Couldn't import file"
          body={importError}
          confirmLabel="OK"
          cancelLabel="Close"
          onCancel={() => setImportError('')}
          onConfirm={() => setImportError('')}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// List row – iOS-Notes style flat row
// ─────────────────────────────────────────────────────────────────────────────
function EntryRow({ entry, allCategories, onOpen }) {
  const cat = allCategories.find((c) => c.id === entry.category);
  const fileCount = entry.files ? Object.keys(entry.files).length : 0;
  const tags = entry.tags ? Object.keys(entry.tags) : [];
  return (
    <li>
      <button type="button" className="vault__entryRow" onClick={onOpen}>
        <div className="vault__entryRowMain">
          <div className="vault__entryRowTop">
            {entry.pinned && (
              <span className="vault__entryPin"><Icon name="pin" size={11} /></span>
            )}
            <span className="vault__entryTitle">{entry.title || 'Untitled'}</span>
            <span className="vault__entryTime">{formatRelative(entry.updatedAt)}</span>
          </div>
          {entry.content && (() => {
            const snippet = stripMarkup(entry.content);
            return snippet ? (
              <p className="vault__entrySnippet">
                {snippet.length > 180 ? `${snippet.slice(0, 180)}…` : snippet}
              </p>
            ) : null;
          })()}
          <div className="vault__entryFoot">
            {cat && <span className="vault__entryCat">{cat.name}</span>}
            {tags.slice(0, 4).map((t) => (
              <span key={t} className="vault__entryTag">#{t}</span>
            ))}
            {tags.length > 4 && <span className="vault__entryTag">+{tags.length - 4}</span>}
            {fileCount > 0 && (
              <span className="vault__entryFileCount">
                <Icon name="paperclip" size={10} /> {fileCount}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry Editor
// ─────────────────────────────────────────────────────────────────────────────
function EntryEditor({ userId, currentUserId, allCategories, entry, isNew, canEdit, onClose, onDeleted, onCreated }) {
  const [title, setTitle] = useState(entry?.title || '');
  const [content, setContent] = useState(entry?.content || '');
  const [category, setCategory] = useState(entry?.category || 'general');
  const [pinned, setPinned] = useState(!!entry?.pinned);
  const [tags, setTags] = useState(entry?.tags ? Object.keys(entry.tags) : []);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');
  const [uploads, setUploads] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(!canEdit);
  const fileInputRef = useRef(null);
  const tagInputRef = useRef(null);
  const entryIdRef = useRef(entry?.id || null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Close cat picker on outside click
  useEffect(() => {
    if (!showCatPicker) return undefined;
    const handler = (e) => {
      if (!e.target.closest?.('.vault__catPill') && !e.target.closest?.('.vault__catPicker')) {
        setShowCatPicker(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [showCatPicker]);

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!canEdit) return;
    if (isNew && entryIdRef.current === null) {
      if (!title.trim() && !content.trim()) return;
    }
    const handle = setTimeout(async () => {
      try {
        if (mountedRef.current) setSaving(true);
        if (mountedRef.current) setError('');
        const tagMap = tags.reduce((acc, t) => { acc[t] = true; return acc; }, {});
        const payload = { title: title.trim() || 'Untitled', content, category, pinned, tags: tagMap };
        if (!entryIdRef.current) {
          const newId = await createEntry(userId, currentUserId, payload);
          entryIdRef.current = newId;
          if (onCreated) onCreated(newId);
        } else {
          await updateEntry(userId, currentUserId, entryIdRef.current, payload);
        }
        if (mountedRef.current) setSavedAt(new Date());
      } catch (e) {
        console.warn('[DataVault] autosave failed', e);
        if (mountedRef.current) setError(e.message || 'Could not save');
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content, category, pinned, tags]);

  const handleAddTag = (raw) => {
    const t = (raw || tagInput).trim().toLowerCase().replace(/^#/, '').replace(/\s+/g, '-');
    if (!t) return;
    if (tags.includes(t)) { setTagInput(''); return; }
    setTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const handleRemoveTag = (t) => setTags((prev) => prev.filter((x) => x !== t));

  const handleFiles = useCallback(async (fileList) => {
    if (!canEdit) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    if (!entryIdRef.current) {
      try {
        const newId = await createEntry(userId, currentUserId, {
          title: title.trim() || 'Untitled',
          content,
          category,
          pinned,
          tags: tags.reduce((acc, t) => { acc[t] = true; return acc; }, {}),
        });
        entryIdRef.current = newId;
        if (onCreated) onCreated(newId);
      } catch (e) {
        if (mountedRef.current) setError(e.message || 'Could not create entry');
        return;
      }
    }

    for (const file of files) {
      const ext = getExtension(file.name);
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        if (mountedRef.current) setError(`File type "${ext || 'unknown'}" is not allowed.`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        if (mountedRef.current) setError(`"${file.name}" exceeds the 25 MB limit.`);
        continue;
      }
      const tempId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (mountedRef.current) {
        setUploads((prev) => [...prev, { tempId, filename: file.name, progress: 0, error: null }]);
      }
      try {
        const meta = await uploadFileToEntry(userId, entryIdRef.current, file, (pct) => {
          if (mountedRef.current) {
            setUploads((prev) => prev.map((u) => u.tempId === tempId ? { ...u, progress: pct } : u));
          }
        });
        const fileRefDb = push(dbRef(database, `World/${userId}/dataVault/entries/${entryIdRef.current}/files`));
        await set(fileRefDb, meta);
        await update(dbRef(database, `World/${userId}/dataVault/entries/${entryIdRef.current}`), {
          updatedAt: new Date().toISOString(),
          updatedBy: currentUserId || null,
        });
        if (mountedRef.current) {
          setUploads((prev) => prev.filter((u) => u.tempId !== tempId));
        }
      } catch (e) {
        if (mountedRef.current) {
          setUploads((prev) => prev.map((u) => u.tempId === tempId ? { ...u, error: e.message || 'Upload failed' } : u));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, userId, currentUserId, title, content, category, pinned, tags]);

  const handleDeleteFile = async (fileId, file) => {
    if (!canEdit || !entryIdRef.current) return;
    try {
      if (file?.storagePath) {
        try { await deleteObject(storageRef(storage, file.storagePath)); }
        catch (_) { /* missing object – proceed with DB cleanup */ }
      }
      await remove(dbRef(database, `World/${userId}/dataVault/entries/${entryIdRef.current}/files/${fileId}`));
    } catch (e) {
      setError(e.message || 'Could not delete file');
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !entryIdRef.current) { onClose(); return; }
    try {
      await deleteEntry(userId, entryIdRef.current, entry?.files || {});
      onDeleted();
    } catch (e) {
      setError(e.message || 'Could not delete entry');
    }
  };

  const handleDragEnter = (e) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };
  const handleDragOver = (e) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };
  const handleDrop = (e) => {
    if (!canEdit) return;
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const filesList = entry?.files
    ? Object.entries(entry.files).map(([id, f]) => ({ id, ...f }))
    : [];

  const currentCat = allCategories.find((c) => c.id === category) || allCategories[allCategories.length - 1];

  return (
    <div
      className={`vault__editor ${dragActive ? 'is-drag-active' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Sub-toolbar: back link + pin/delete icons */}
      <div className="vault__editorBar">
        <button type="button" className="vault__editorBack" onClick={onClose} aria-label="Back to list">
          <Icon name="arrow-left" size={13} />
          <span>All entries</span>
        </button>
        <div className="vault__editorBarActions">
          {canEdit && (
            <>
              <button
                type="button"
                className={`vault__macIconBtn ${pinned ? 'is-active' : ''}`}
                onClick={() => setPinned((p) => !p)}
                title={pinned ? 'Unpin' : 'Pin to top'}
                aria-pressed={pinned}
              >
                <Icon name="pin" size={14} />
              </button>
              {!isNew && entryIdRef.current && (
                <button
                  type="button"
                  className="vault__macIconBtn vault__macIconBtn--danger"
                  onClick={() => setConfirmDelete(true)}
                  title="Delete entry"
                >
                  <Icon name="trash" size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <input
        type="text"
        className="vault__titleInput"
        placeholder="Untitled"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        disabled={!canEdit}
      />

      <div className="vault__inlineMeta">
        <button
          type="button"
          className="vault__catPill"
          onClick={() => canEdit && setShowCatPicker((s) => !s)}
          disabled={!canEdit}
          title="Change category"
        >
          <Icon name={currentCat.icon} size={11} />
          <span>{currentCat.name}</span>
          {canEdit && <Icon name="chevron-down" size={9} className="vault__catPillCaret" strokeWidth={2.2} />}
        </button>
        {showCatPicker && canEdit && (
          <div className="vault__catPicker" role="menu">
            {allCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                role="menuitem"
                className={`vault__catPickerItem ${c.id === category ? 'is-active' : ''}`}
                onClick={() => { setCategory(c.id); setShowCatPicker(false); }}
              >
                <Icon name={c.icon} size={12} />
                <span>{c.name}</span>
                {c.id === category && <Icon name="check" size={11} className="vault__catPickerCheck" strokeWidth={2.2} />}
                {c.custom && <span className="vault__catPickerCustom">custom</span>}
              </button>
            ))}
          </div>
        )}

        <span className="vault__inlineDot" aria-hidden />

        <div className="vault__inlineTags">
          {tags.map((t) => (
            <span key={t} className="vault__inlineTag">
              #{t}
              {canEdit && (
                <button type="button" onClick={() => handleRemoveTag(t)} aria-label={`Remove ${t}`}>
                  <Icon name="x" size={8} strokeWidth={2.4} />
                </button>
              )}
            </span>
          ))}
          {canEdit && (showTagInput ? (
            <input
              ref={tagInputRef}
              type="text"
              className="vault__inlineTagInput"
              placeholder="tag…"
              value={tagInput}
              autoFocus
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
                  e.preventDefault();
                  handleAddTag();
                } else if (e.key === 'Escape') {
                  setTagInput('');
                  setShowTagInput(false);
                } else if (e.key === 'Backspace' && !tagInput && tags.length) {
                  handleRemoveTag(tags[tags.length - 1]);
                }
              }}
              onBlur={() => {
                if (tagInput) handleAddTag();
                setShowTagInput(false);
              }}
            />
          ) : (
            <button
              type="button"
              className="vault__inlineTagAdd"
              onClick={() => setShowTagInput(true)}
              title="Add tag"
            >
              <Icon name="plus" size={9} strokeWidth={2.2} />
              tag
            </button>
          ))}
        </div>
      </div>

      {category === HEADER_NOTES_CATEGORY_ID && (
        <div className="vault__headerNoteHint">
          <Icon name="sticky-note" size={13} />
          <span>
            Shows as a note icon in the top navigation. Write a short intro,
            then add key points as bullets with <code>-</code>.
          </span>
        </div>
      )}

      {canEdit && (
        <div className="vault__contentTabs">
          <button
            type="button"
            className={`vault__contentTab ${!preview ? 'is-active' : ''}`}
            onClick={() => setPreview(false)}
          >
            <Icon name="pencil" size={11} /> Write
          </button>
          <button
            type="button"
            className={`vault__contentTab ${preview ? 'is-active' : ''}`}
            onClick={() => setPreview(true)}
          >
            <Icon name="eye" size={11} /> Preview
          </button>
          <span className="vault__contentTabsHint">
            Supports <code>**bold**</code>, <code>_italic_</code>, <code># heading</code>, <code>- list</code>, <code>&gt; callout</code>
          </span>
        </div>
      )}

      {preview ? (
        (() => {
          const rendered = renderRichText(content);
          return rendered
            ? <div className="vault__rich">{rendered}</div>
            : <div className="vault__richEmpty">Nothing to preview yet.</div>;
        })()
      ) : (
        <textarea
          className="vault__contentInput"
          placeholder={canEdit
            ? (category === HEADER_NOTES_CATEGORY_ID
                ? 'A few sentences of context first…\n\n- Key point one\n- Key point two\n- Key point three'
                : 'Drop the long-term knowledge here. Be specific – this is the source you\'ll re-use for campaigns.')
            : 'No content.'}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={!canEdit}
          rows={10}
        />
      )}

      <div className="vault__filesSection">
        <div className="vault__filesHead">
          <span className="vault__filesLabel">
            <Icon name="paperclip" size={12} />
            Files
            {filesList.length > 0 && <span className="vault__filesCount">{filesList.length}</span>}
          </span>
          {canEdit && (
            <button
              type="button"
              className="vault__macTextBtn"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="plus" size={11} strokeWidth={2.2} /> Add
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFiles(e.target.files);
              if (e.target) e.target.value = '';
            }}
          />
        </div>

        {filesList.length === 0 && uploads.length === 0 ? (
          canEdit ? (
            <button
              type="button"
              className="vault__dropZone"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="cloud-upload" size={18} />
              <span>Drop files here or <em>click to browse</em></span>
              <small>PDF, DOCX, XLSX, images and more · 25 MB max each</small>
            </button>
          ) : (
            <div className="vault__filesEmpty">No files attached.</div>
          )
        ) : (
          <ul className="vault__filesList">
            {filesList.map((f) => (
              <li key={f.id} className="vault__fileRow">
                <span className="vault__fileIcon">
                  <Icon name={fileIconFor(getExtension(f.filename))} size={15} />
                </span>
                <a
                  href={f.downloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vault__fileName"
                  title={f.filename}
                >
                  {f.filename}
                </a>
                <span className="vault__fileSize">{formatSize(f.size)}</span>
                {canEdit && (
                  <button
                    type="button"
                    className="vault__macIconBtn vault__macIconBtn--small"
                    onClick={() => handleDeleteFile(f.id, f)}
                    aria-label={`Remove ${f.filename}`}
                    title="Remove"
                  >
                    <Icon name="x" size={11} strokeWidth={2.2} />
                  </button>
                )}
              </li>
            ))}
            {uploads.map((u) => (
              <li key={u.tempId} className="vault__fileRow vault__fileRow--uploading">
                <span className="vault__fileIcon">
                  <Icon name="cloud-upload" size={15} />
                </span>
                <span className="vault__fileName">{u.filename}</span>
                {u.error
                  ? <span className="vault__fileErr">{u.error}</span>
                  : <span className="vault__fileProgress">{u.progress}%</span>
                }
              </li>
            ))}
          </ul>
        )}
      </div>

      <footer className="vault__editorFoot">
        {error
          ? <span className="vault__editorErr">
              <Icon name="alert-circle" size={11} /> {error}
            </span>
          : saving
            ? <span className="vault__editorSaving">
                <span className="vault__savingDot" aria-hidden /> Saving…
              </span>
            : savedAt
              ? <span className="vault__editorSaved">Saved {formatRelative(savedAt.toISOString())}</span>
              : <span className="vault__editorIdle">Auto-saves as you type</span>}
      </footer>

      {dragActive && canEdit && (
        <div className="vault__dropOverlay" aria-hidden>
          <div className="vault__dropOverlayInner">
            <Icon name="cloud-upload" size={22} />
            <span>Drop to attach</span>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete this entry?"
          body="The entry and all attached files will be removed permanently. This can't be undone."
          confirmLabel="Delete"
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable confirm dialog
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDialog({ title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="vault__confirmBackdrop" onClick={onCancel}>
      <div className="vault__confirmCard" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {body && <p>{body}</p>}
        <div className="vault__confirmActions">
          <button type="button" className="vault__confirmCancel" onClick={onCancel}>{cancelLabel}</button>
          <button
            type="button"
            className={danger ? 'vault__confirmDelete' : 'vault__confirmPrimary'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom category manager
// ─────────────────────────────────────────────────────────────────────────────
function CategoryManager({ userId, customCategories, entriesArr, onClose }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [pendingRemove, setPendingRemove] = useState(null);

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed.length > 60) { setError('Name is too long.'); return; }
    try {
      await addCustomCategory(userId, trimmed);
      setName('');
      setError('');
    } catch (e) {
      setError(e.message || 'Could not add category');
    }
  };

  const handleRemoveConfirm = async (catId) => {
    try {
      await removeCustomCategory(userId, catId);
      setPendingRemove(null);
    } catch (e) {
      setError(e.message || 'Could not remove category');
    }
  };

  const customList = Object.entries(customCategories || {}).map(([id, c]) => ({ id, ...c }));

  return (
    <div className="vault__confirmBackdrop" onClick={onClose}>
      <div className="vault__catManager" onClick={(e) => e.stopPropagation()}>
        <header className="vault__catManagerHead">
          <h3>Manage categories</h3>
          <button type="button" className="vault__macClose" onClick={onClose} aria-label="Close">
            <Icon name="x" size={11} strokeWidth={2.2} />
          </button>
        </header>
        <p className="vault__catManagerHint">
          Default categories are always available. Add your own to organise account knowledge the way your team thinks.
        </p>
        <div className="vault__catAddRow">
          <input
            type="text"
            className="vault__catAddInput"
            placeholder="New category (e.g. Compliance, Internal SOPs)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
          <button type="button" className="vault__primaryBtn" onClick={handleAdd}>
            <Icon name="plus" size={12} strokeWidth={2.2} /> Add
          </button>
        </div>
        {error && <div className="vault__catErr">{error}</div>}
        <ul className="vault__catList">
          {customList.length === 0 && (
            <li className="vault__catEmpty">No custom categories yet.</li>
          )}
          {customList.map((c) => {
            const usedCount = entriesArr.filter((e) => e.category === c.id).length;
            return (
              <li key={c.id} className="vault__catRow">
                <span className="vault__catIcon"><Icon name="folder" size={13} /></span>
                <span className="vault__catName">{c.name}</span>
                <span className="vault__catUsed">{usedCount} {usedCount === 1 ? 'entry' : 'entries'}</span>
                <button
                  type="button"
                  className="vault__macIconBtn vault__macIconBtn--small vault__macIconBtn--danger"
                  onClick={() => setPendingRemove({ id: c.id, name: c.name, used: usedCount })}
                  aria-label={`Remove ${c.name}`}
                  title="Remove"
                >
                  <Icon name="trash" size={12} />
                </button>
              </li>
            );
          })}
        </ul>
        <div className="vault__catDefaults">
          <span className="vault__catDefaultsLabel">Built-in</span>
          {DEFAULT_CATEGORIES.map((c) => (
            <span key={c.id} className="vault__catDefaultChip" title={c.hint}>
              <Icon name={c.icon} size={10} /> {c.name}
            </span>
          ))}
        </div>

        {pendingRemove && (
          <ConfirmDialog
            title={`Remove "${pendingRemove.name}"?`}
            body={pendingRemove.used > 0
              ? `${pendingRemove.used} ${pendingRemove.used === 1 ? 'entry uses' : 'entries use'} this category. They'll keep their reference but the category disappears from the list.`
              : 'This category will be removed.'}
            confirmLabel="Remove"
            danger
            onCancel={() => setPendingRemove(null)}
            onConfirm={() => handleRemoveConfirm(pendingRemove.id)}
          />
        )}
      </div>
    </div>
  );
}
